<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { app, os } from 'src/stores/session/native-bridge';
	import {
		AdvancedCardV2,
		ButtonV2,
		CircularProgress,
		Modal,
		Text,
	} from '@Shelly/shelly-design-system/index';
	import { get_ip, get_local_ip, get_ssid, real_id } from 'src/helpers/interface_helpers';
	import { open_external_link } from 'src/services/native_container_requests_ios';
	import { open_external_link_android } from 'src/services/native_container_requests_android';
	import {
		getStorageError,
		STORAGE_ERROR,
		type NfsConfig,
		type StorageStatus,
	} from 'src/components/Camera/types/camera-storage';
	import {
		ejectStorage,
		formatStorage,
		getStorageComponentKey,
		getStorageSeverity,
		getStorageStateLabel,
		getStorageUsage,
		isStorageBusy,
		setStorageNfsConfig,
	} from 'src/components/Camera/helpers/camera-storage-helpers';
	import NfsSetupModal from './NfsSetupModal.svelte';

	export let device: Device;

	const realId = real_id(device.id);
	const storageKey = getStorageComponentKey(device);

	const mutedSubtitle = (value: string) => ({
		type: 'muted' as const,
		bold: false,
		textSmall: true,
		value,
	});

	let showNfsModal = false;
	let showFormatModal = false;
	let formatLoading = false;
	let ejectLoading = false;
	let nfsRemoveLoading = false;

	$: statusStore = getDeviceStatus(realId);
	$: storageStatus = $statusStore?.[storageKey] as StorageStatus | undefined;
	$: settingsStore = getDeviceSettings(realId);
	$: nfsConfig = $settingsStore?.[storageKey]?.nfs as NfsConfig | undefined;
	$: storageId = storageStatus?.id ?? (device?.channel || 0);
	$: usage = getStorageUsage(storageStatus);
	$: stateLabel = getStorageStateLabel(storageStatus);
	$: isFormatting =
		storageStatus?.errors?.some((error) => getStorageError(error) === STORAGE_ERROR.FORMATTING) ??
		false;
	$: storageBusy = isStorageBusy(storageStatus);
	$: nfsConfigured = !!(nfsConfig?.host || nfsConfig?.path);

	$: usedPercent = storageStatus?.active ? usage.used_percent : 0;
	$: storageProgressType = getStorageSeverity(usage.free_percent);

	$: usageText = $format('camera_storage_usage', {
		values: { used: usage.used_label, total: usage.total_label, free: usage.free_percent },
		default: `${usage.used_label} / ${usage.total_label} (${usage.free_percent}% free)`,
	});

	$: overviewSubtitle = storageStatus?.active ? mutedSubtitle(usageText) : undefined;

	$: overviewDescription = stateLabel
		? mutedSubtitle($format(stateLabel.key, { default: stateLabel.default }))
		: undefined;

	$: nfsSubtitle = mutedSubtitle(
		nfsConfigured
			? (nfsConfig?.host ?? '')
			: $format('camera_storage_nfs_desc', { default: 'Network file system configuration' }),
	);

	$: localIp = get_local_ip(device.id) || (!$app && get_ip($statusStore)) || false;
	$: canOpenLocal = typeof localIp === 'string' && /\d/.test(localIp);
	$: deviceSsid = get_ssid($settingsStore, $statusStore, device);

	const getLocalManagementDescription = (
		canOpen: boolean,
		isApp: boolean,
		ssid: string | undefined,
	) => {
		if (canOpen && isApp) {
			return $format('camera_storage_local_mgmt_desc', {
				default: 'Access the on-device Web UI',
			});
		}
		if (ssid) {
			return $format('camera_storage_local_mgmt_connect_named', {
				values: { ssid },
				default: `Connect to Wi-Fi “${ssid}”`,
			});
		}
		return $format('camera_storage_local_mgmt_connect', {
			default: 'Connect to the device’s Wi-Fi',
		});
	};

	const openLocalWebUi = () => {
		if (typeof localIp !== 'string') return;
		const url = `http://${localIp}`;
		if ($app) {
			if ($os === 'IOS') open_external_link(url);
			else open_external_link_android(url);
		} else {
			window.open(url);
		}
	};

	const confirmFormat = async () => {
		formatLoading = true;
		const success = await formatStorage(device);
		formatLoading = false;
		if (success) showFormatModal = false;
	};

	const onEject = async () => {
		ejectLoading = true;
		await ejectStorage(device);
		ejectLoading = false;
	};

	const onRemoveNfs = async () => {
		nfsRemoveLoading = true;
		await setStorageNfsConfig(
			device,
			null,
			$format('camera_storage_nfs_removed_toast', { default: 'NFS share removed.' }),
		);
		nfsRemoveLoading = false;
	};
</script>

<AdvancedCardV2
	class="mb-s"
	aria-label={$format('camera_storage_overview_title', { default: 'Storage' })}
	title={`${$format('camera_storage_overview_title', { default: 'Storage' })} (${storageId})`}
	avatar={{ icon: 'fal fa-hard-drive' }}
	subtitle={overviewSubtitle}
	description={overviewDescription}
>
	<div slot="right" class="d-flex align-items-center">
		{#if storageBusy}
			<CircularProgress size="m" type="warning" icon="fal fa-arrows-rotate" />
		{:else if storageStatus?.active}
			<CircularProgress size="m" progress={usedPercent} type={storageProgressType} />
		{/if}
	</div>
</AdvancedCardV2>

<AdvancedCardV2
	class="mb-s"
	aria-label={$format('camera_storage_format_title', { default: 'Format storage' })}
	title={$format('camera_storage_format_title', { default: 'Format storage' })}
	avatar={{ icon: 'fal fa-eraser' }}
	subtitle={mutedSubtitle($format('camera_storage_format_desc', { default: 'Erase all stored data' }))}
>
	<div slot="right" class="d-flex align-items-center">
		<ButtonV2
			size="s"
			variant="danger"
			disabled={!storageStatus?.present || isFormatting || formatLoading}
			on:click={() => (showFormatModal = true)}
			aria-label={$format('button_camera_storage_format', { default: 'Format' })}
		>
			{$format('button_camera_storage_format', { default: 'Format' })}
		</ButtonV2>
	</div>
</AdvancedCardV2>

<AdvancedCardV2
	class="mb-s"
	aria-label={$format('camera_storage_eject_title', { default: 'Eject storage' })}
	title={$format('camera_storage_eject_title', { default: 'Eject storage' })}
	avatar={{ icon: 'fal fa-eject' }}
	subtitle={mutedSubtitle(
		$format('camera_storage_eject_desc', { default: 'Safely eject the storage device' }),
	)}
>
	<div slot="right" class="d-flex align-items-center">
		<ButtonV2
			size="s"
			variant="secondary"
			loading={ejectLoading}
			disabled={!storageStatus?.active || ejectLoading}
			on:click={onEject}
			aria-label={$format('button_camera_storage_eject', { default: 'Eject' })}
		>
			{$format('button_camera_storage_eject', { default: 'Eject' })}
		</ButtonV2>
	</div>
</AdvancedCardV2>

<AdvancedCardV2
	class="mb-s"
	aria-label={$format('camera_storage_nfs_title', { default: 'NFS' })}
	title={$format('camera_storage_nfs_title', { default: 'NFS' })}
	avatar={{ icon: 'fal fa-network-wired' }}
	subtitle={nfsSubtitle}
>
	<div slot="right" class="d-flex align-items-center">
		<ButtonV2
			class={nfsConfigured ? 'mr-s' : ''}
			size="s"
			variant="secondary"
			on:click={() => (showNfsModal = true)}
			aria-label={$format('camera_storage_nfs_setup', { default: 'Set up NFS' })}
		>
			{nfsConfigured
				? $format('button_edit', { default: 'Edit' })
				: $format('button_set_up', { default: 'Set up' })}
		</ButtonV2>
		{#if nfsConfigured}
			<ButtonV2
				size="s"
				variant="danger-secondary"
				loading={nfsRemoveLoading}
				disabled={nfsRemoveLoading}
				on:click={onRemoveNfs}
				aria-label={$format('camera_storage_nfs_remove', { default: 'Remove NFS' })}
			>
				{$format('button_remove', { default: 'Remove' })}
			</ButtonV2>
		{/if}
	</div>
</AdvancedCardV2>

<AdvancedCardV2
	aria-label={$format('camera_storage_local_mgmt_title', {
		default: 'Local Recordings',
	})}
	title={$format('camera_storage_local_mgmt_title', { default: 'Local Recordings' })}
	avatar={{ icon: 'fal fa-browser' }}
	subtitle={mutedSubtitle(getLocalManagementDescription(canOpenLocal, $app, deviceSsid))}
>
	<div slot="right" class="d-flex align-items-center">
		<ButtonV2
			size="s"
			variant="secondary"
			icon="fal fa-arrow-up-right-from-square"
			disabled={!canOpenLocal}
			on:click={openLocalWebUi}
			aria-label={$format('camera_storage_local_mgmt_open', { default: 'Open Web UI' })}
		>
			{$format('camera_storage_local_mgmt_open', { default: 'Open Web UI' })}
		</ButtonV2>
	</div>
</AdvancedCardV2>

<NfsSetupModal
	{device}
	nfs={nfsConfig}
	showModal={showNfsModal}
	on:close={() => (showNfsModal = false)}
/>

<Modal
	showModal={showFormatModal}
	title={$format('camera_storage_format_confirm_title', { default: 'Format storage?' })}
	backdropClose={false}
	on:close={() => (showFormatModal = false)}
>
	<svelte:fragment slot="content">
		<Text>
			{$format('camera_storage_format_confirm_message', {
				default:
					'This will permanently delete all data on the SD card. This action cannot be undone.',
			})}
		</Text>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			class="mr-s"
			variant="secondary"
			on:click={() => (showFormatModal = false)}
			aria-label={$format('button_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			variant="danger"
			loading={formatLoading}
			on:click={confirmFormat}
			aria-label={$format('button_camera_storage_format', { default: 'Format' })}
		>
			{$format('button_camera_storage_format', { default: 'Format' })}
		</ButtonV2>
	</div>
</Modal>
